import React, { useRef, useCallback, useState } from 'react';
import Input2 from '../input/Input2';
import usePlacesAutocomplete, { Suggestion, getDetails } from 'use-places-autocomplete';
import styled from 'styled-components';
import * as styles from './styles';
import { IAddressAutocompleteProps } from './models';
import { useOnClickOutside } from '../../../helpers/customHooks';
import { getGoogleAddress } from '../../../helpers/googlePlacesAutocompleteHelper';
import PlaceResult = google.maps.places.PlaceResult;
import locationIcon from '../../assets/icon-place.svg';
import IAddress from '@sunbit-dev/common/models/IAddress';

const AddressAutocomplete = styled((props: IAddressAutocompleteProps) => {
  const { name, onChange, onSelect } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [activeSuggestionIndex, setActiveIndex] = useState<number>(0);
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: {
        country: ['us'],
      },
    },
    debounce: 300,
  });

  const handleBlurOnSelectBox = useCallback(() => {
    setActiveIndex(0);
    onChange(value, name);
    clearSuggestions();
  }, [value, name]);

  const onKeyUp = (e: React.KeyboardEvent) => {
    if (status === 'OK') {
      switch (e.key) {
        case 'ArrowUp':
          if (activeSuggestionIndex != 0) {
            onChange(data[activeSuggestionIndex - 1].description, name);
            setActiveIndex((prev) => prev - 1);
          }
          break;
        case 'ArrowDown':
          if (activeSuggestionIndex + 1 !== data.length) {
            const val = data[activeSuggestionIndex + 1].description;
            onChange(val, name);
            setActiveIndex((prev) => prev + 1);
          }
          break;
        case 'Enter':
          handleSelect(data[activeSuggestionIndex])();
          break;
        case 'Escape':
          setActiveIndex(0);
          onChange(value, name);
          clearSuggestions();
      }
    }
  };

  useOnClickOutside(ref, handleBlurOnSelectBox);
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Update the keyword of the input element
    setValue(e.target.value);
    onChange(e.target.value, name);
  };

  const handleSelect = (suggestion: Suggestion) => async () => {
    const details = await getDetails({
      placeId: suggestion.place_id,
    });
    let address: IAddress;

    if (typeof details != 'string') {
      address = getGoogleAddress(details as PlaceResult);
      onSelect(address);
    }
    setValue(suggestion.description, false);
    setActiveIndex(0);
    clearSuggestions();
  };

  const renderSuggestions = () => {
    return data.map((suggestion, index) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
        matched_substrings,
      } = suggestion;
      const numOfMatched = matched_substrings.length;
      let matchedStr: string = '';

      for (let i: number = 0; i < numOfMatched - 1; i++) {
        const elem = matched_substrings[i];
        //if matched_substrings has more than one matched then each one of the elements represent a different word
        matchedStr += main_text.substring(elem.offset, elem.offset + elem.length) + ' ';
      }

      const lastMatched = matched_substrings[numOfMatched - 1];
      //last matched word not require
      matchedStr += main_text.substring(lastMatched.offset, lastMatched.offset + lastMatched.length);
      const reminder: string = main_text.split(matchedStr)[1];
      return (
        <div className={index === activeSuggestionIndex ? 'active-suggestion' : 'suggestion'} data-at={`${props.name}-suggestion-${index}`} key={place_id} onClick={handleSelect(suggestion)}>
          <div className={'suggestion-container'}>
            <div className={'location-icon'}>
              <img src={locationIcon} />
            </div>
            <div className={'suggestion-text-container'}>
              <div className={'suggestion-text-main'}>
                <p className={'matched-pattern'}>{matchedStr}</p>
                <p>{reminder}</p>
              </div>
              <div className={'suggestion-secondary'}>{secondary_text}</div>
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className={props.className} data-at={`address-autocomplete`}>
      <Input2
        errorLabelAbsolutePosition={false}
        error={props.error}
        label={props.label}
        onKeyUp={onKeyUp}
        color={'primary'}
        className={props.className}
        dataAt={'address'}
        name={name}
        value={props.value}
        onChange={handleInput}
        disabled={!ready}
        placeholder="Please enter address"
      />
      {status === 'OK' && (
        <div data-at={`address-autocomplete-menu-list`} ref={ref} className={`menu-list`}>
          {renderSuggestions()}
        </div>
      )}
    </div>
  );
})`
  ${styles.SAddressContainer}
`;

export default AddressAutocomplete;
